import { SearchIcon } from '@chakra-ui/icons';
import {
    Checkbox,
    FormControl,
    FormLabel,
    Input,
    InputGroup,
    InputLeftElement,
    Stack,
    Text,
    useToast,
} from '@chakra-ui/react';
import FormModal from '../../../components/FormModal';
import { useMemo, useState } from 'react';
import { translate } from '../../../i18n';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import { classifyRead, scopePromptI18nKey } from '../../_shared/readState';
import { useGetUsers } from '../../user-management/hooks/useGetUsers';
import { useAssignCoupon } from '../hooks/useAssignCoupon';
import { matchesNameQuery } from '../../../utils/name-search';

interface AssignCouponModalProps {
    couponId: string;
    couponName: string;
    isOpen: boolean;
    onClose: () => void;
}

export default function AssignCouponModal({ couponId, couponName, isOpen, onClose }: AssignCouponModalProps) {
    const { selectedOrganization } = useSelectedOrganization();
    // Gated on `isOpen` — CouponDetailPage mounts this modal as soon as the
    // coupon loads, so viewing a coupon pulled the whole roster (DASHBOARD-Z).
    // R92-5 — `isError` is taken. Post-R89-1 a refused `GET /users` leaves
    // `data === undefined`, `filteredUsers` falls to `[]`, and this modal —
    // opened to assign a coupon to somebody — reported "no users found" with a
    // live "Assign to 0 users" button beside it.
    //
    // R118-3 — R92-5 closed the SECOND term of a two-term gate and left the
    // first (D72). `useGetUsers` is `enabled: Boolean(organizationId) &&
    // (options?.enabled ?? true)`; this call site supplies only `enabled:
    // isOpen`. Nothing on the path closes the organization term: `CouponList`
    // early-returns a `ScopePrompt` before mounting this, but the OTHER caller,
    // `CouponDetailPage`, is gated on `couponId` from the route and reads no
    // organization at all — so a coupon reached by link with nothing selected
    // opened this dialog onto a roster that was never requested and said "No
    // users found" about it, beside a live "Assign to 0 users" button. The
    // in-flight arm comes with it: this is the unpaginated org roster
    // (DASHBOARD-Z) and the same sentence showed for its whole duration.
    const {
        data: users,
        isInitialLoading: isUsersLoading,
        isError: isUsersError,
    } = useGetUsers(selectedOrganization?.id, undefined, {
        enabled: isOpen,
    });
    const usersReadState = classifyRead(isUsersError, isUsersLoading, users);
    const { mutateAsync: assignCoupon, isLoading } = useAssignCoupon();
    const toast = useToast();

    const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
    const [searchQuery, setSearchQuery] = useState('');

    const filteredUsers = useMemo(() => {
        if (!users) return [];
        if (!searchQuery) return users;

        return users.filter((user) => matchesNameQuery(searchQuery, user));
    }, [users, searchQuery]);

    const handleToggleUser = (userId: string) => {
        setSelectedUserIds((prev) =>
            prev.includes(userId)
                ? prev.filter((id) => id !== userId)
                : [...prev, userId],
        );
    };

    const handleToggleAll = () => {
        if (selectedUserIds.length === filteredUsers.length) {
            setSelectedUserIds([]);
        } else {
            setSelectedUserIds(filteredUsers.map((u) => u.id));
        }
    };

    const handleSubmit = async () => {
        if (selectedUserIds.length === 0) {
            toast({
                title: translate('coupon.no_users_selected'),
                status: 'warning',
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        try {
            await assignCoupon({
                couponId,
                userIds: selectedUserIds,
            });
            toast({
                title: translate('coupon.assigned_successfully').replace('{count}', String(selectedUserIds.length)),
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
            setSelectedUserIds([]);
            setSearchQuery('');
            onClose();
        } catch (error: any) {
            toast({
                title: translate('coupon.assign_error'),
                description: error.message || '',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const onModalClose = () => {
        setSelectedUserIds([]);
        setSearchQuery('');
        onClose();
    };

    return (
        <FormModal
            isOpen={isOpen}
            onClose={onModalClose}
            title={translate('coupon.assign_to').replace('{name}', couponName)}
            size="xl"
            onSubmit={handleSubmit}
            submitLabel={translate('coupon.assign_to_users').replace('{count}', String(selectedUserIds.length))}
            cancelLabel={translate('common.cancel')}
            isSubmitting={isLoading}
            isSubmitDisabled={selectedUserIds.length === 0}
        >
            <FormControl mb={4}>
                <FormLabel>{translate('coupon.search_users')}</FormLabel>
                <InputGroup>
                    <InputLeftElement pointerEvents="none">
                        <SearchIcon color="gray.400" />
                    </InputLeftElement>
                    <Input
                        placeholder={translate('coupon.search_by_name_or_email')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </InputGroup>
            </FormControl>

            <FormControl>
                <Checkbox
                    isChecked={
                        filteredUsers.length > 0 &&
                        selectedUserIds.length === filteredUsers.length
                    }
                    onChange={handleToggleAll}
                    mb={2}
                >
                    <Text fontWeight="bold">
                        {translate('cohort.select_all')} ({filteredUsers.length})
                    </Text>
                </Checkbox>
            </FormControl>

            <Stack spacing={2} maxH="400px" overflowY="auto" mt={4}>
                {filteredUsers.map((user) => (
                    <Checkbox
                        key={user.id}
                        isChecked={selectedUserIds.includes(user.id)}
                        onChange={() => handleToggleUser(user.id)}
                    >
                        <Text>
                            {user.firstName} {user.lastName} ({user.email})
                        </Text>
                    </Checkbox>
                ))}

                {filteredUsers.length === 0 && (
                    <Text
                        color={usersReadState === 'unreadable' ? 'danger.500' : 'gray.500'}
                        textAlign="center"
                        py={8}
                    >
                        {usersReadState === 'unreadable'
                            ? translate('user.error_loading')
                            : usersReadState === 'loading'
                                ? translate('cohort.loading')
                                : usersReadState === 'never-issued'
                                    // Borrowed key (R96-7 freeze); a text line
                                    // rather than a `ScopePrompt` panel, as one
                                    // control inside a form (run 114's call).
                                    ? translate(scopePromptI18nKey('organization') as any)
                                    : translate('cohort.no_users_found')}
                    </Text>
                )}
            </Stack>

            <Text mt={4} color="gray.600" fontSize="sm">
                {selectedUserIds.length} {translate('cohort.users_selected')}
            </Text>
        </FormModal>
    );
}
